import React, { useEffect, useMemo, useState } from "react";
import { db } from "@/config/firebase";
import { collection, getDoc, getDocs, query, where, doc as fsDoc, addDoc, serverTimestamp, Timestamp, orderBy, limit } from "firebase/firestore";
import { useAuth } from "@/context/AuthContext";
import { calcOCEFromGame, calcKCE } from "@/utils/scoring";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { PlayerAvatar } from "@/components/PlayerAvatar";

function parseDate(v){
  if (v?.toDate) return v.toDate();
  if (typeof v === 'string') return new Date(v);
  return null;
}

export default function Profile() {
  const { user } = useAuth();
  const [me, setMe] = useState(null);
  const [gs, setGs] = useState([]);
  const [events, setEvents] = useState([]);
  const [signups, setSignups] = useState([]);
  const [kdays, setKdays] = useState([]);
  const [ksessions, setKsessions] = useState([]);
  const [bets, setBets] = useState([]);
  const [users, setUsers] = useState([]);
  const [openMvpEvents, setOpenMvpEvents] = useState([]);
  const [showMvpModal, setShowMvpModal] = useState(false);
  const [selectedMvpEvent, setSelectedMvpEvent] = useState(null);
  const [mvpVote, setMvpVote] = useState("");

  useEffect(()=>{(async()=>{
    if (!user) return;
    const meSnap = await getDoc(fsDoc(db, 'users', user.uid));
    setMe(meSnap.exists()? meSnap.data(): {});
    const g = await getDocs(query(collection(db, "game_stats"), where("player_id","==", user.uid)));
    setGs(g.docs.map(d=>({id:d.id, ...d.data()})));
    const ev = await getDocs(collection(db, "events"));
    setEvents(ev.docs.map(d=>({ id:d.id, ...d.data()})));
    const su = await getDocs(query(collection(db, "event_signups"), where("user_id","==", user.uid)));
    setSignups(su.docs.map(d=>({ id:d.id, ...d.data()})));
    const kd = await getDocs(collection(db, "kiosk_day"));
    setKdays(kd.docs.map(d=>({id:d.id, ...d.data()})).filter(x=>x.user_id===user.uid));
    const ks = await getDocs(collection(db, "kiosk_sessions"));
    setKsessions(ks.docs.map(d=>({id:d.id, ...d.data()})).filter(s=> (s.user_ids||[]).includes(user.uid)));
    const b = await getDocs(query(collection(db, "bets"), where("user_id","==", user.uid)));
    setBets(b.docs.map(d=>({id:d.id, ...d.data()})));
    
    // Load all users for MVP voting
    const usersSnap = await getDocs(collection(db, "users"));
    setUsers(usersSnap.docs.map(d=>({ id:d.id, ...d.data()})));
    
    // Check for open MVP votes
    await checkOpenMvpVotes();
  })();},[user]);
  
  const checkOpenMvpVotes = async () => {
    if (!user) return;
    try {
      // Get past events (last 2 weeks)
      const twoWeeksAgo = new Date();
      twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14);
      
      const eventsSnap = await getDocs(
        query(
          collection(db, "events"),
          where("startTime", ">=", Timestamp.fromDate(twoWeeksAgo)),
          where("startTime", "<=", Timestamp.now())
        )
      );
      
      // Get my signups
      const signupsSnap = await getDocs(
        query(collection(db, "event_signups"), where("user_id", "==", user.uid))
      );
      const myEventIds = signupsSnap.docs.map(d => d.data().event_id);
      
      // Get existing votes
      const votesSnap = await getDocs(
        query(collection(db, "shower_gel_mvp"), where("voter_id", "==", user.uid))
      );
      const votedEventIds = votesSnap.docs.map(d => d.data().event_id);
      
      // Get next event
      const nextEventsSnap = await getDocs(
        query(
          collection(db, "events"),
          where("startTime", ">", Timestamp.now()),
          orderBy("startTime", "asc"),
          limit(1)
        )
      );
      const nextEventTime = nextEventsSnap.docs[0]?.data()?.startTime;
      
      // Find events I attended but haven't voted for yet
      const openEvents = eventsSnap.docs
        .filter(d => {
          const eventId = d.id;
          const eventData = d.data();
          const eventTime = eventData.startTime?.toDate();
          
          // Must have attended
          if (!myEventIds.includes(eventId)) return false;
          
          // Must not have voted yet
          if (votedEventIds.includes(eventId)) return false;
          
          // Event must be in the past
          if (eventTime > new Date()) return false;
          
          // If there's a next event, voting closes when next event starts
          if (nextEventTime && eventTime < nextEventTime.toDate()) {
            return true;
          }
          
          // Otherwise, voting open for 7 days
          const sevenDaysAgo = new Date();
          sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
          return eventTime > sevenDaysAgo;
        })
        .map(d => ({ id: d.id, ...d.data() }));
      
      setOpenMvpEvents(openEvents);
    } catch (error) {
      console.error("Error checking MVP votes:", error);
    }
  };
  
  const submitMvpVote = async () => {
    if (!mvpVote || !selectedMvpEvent) {
      alert("Bitte wähle einen Spieler aus!");
      return;
    }
    
    try {
      await addDoc(collection(db, "shower_gel_mvp"), {
        event_id: selectedMvpEvent.id,
        event_title: selectedMvpEvent.title,
        player_id: mvpVote,
        voter_id: user.uid,
        created_at: serverTimestamp()
      });
      
      alert("✅ Dusch-Gel MVP Vote abgegeben!");
      setShowMvpModal(false);
      setMvpVote("");
      setSelectedMvpEvent(null);
      
      // Refresh open votes
      await checkOpenMvpVotes();
    } catch (error) {
      alert(`❌ Fehler: ${error.message}`);
    }
  };

  // Games totals and series (only via game_stats)
  const gamesTotal = useMemo(()=> new Set(gs.map(x=>x.game_id)).size, [gs]);
  const gamesSeries = useMemo(()=>{
    // Consider only events of type 'game' sorted by date asc, count tail where user has stats
    const games = events.filter(e=>e.type==='game').map(e=>({ ...e, _dt: parseDate(e.start_datetime)})).filter(e=>e._dt).sort((a,b)=>a._dt-b._dt);
    const playedIds = new Set(gs.map(x=>x.game_id));
    let count = 0;
    for (let i = games.length-1; i>=0; i--) {
      const ev = games[i];
      if (ev.status && ev.status !== 'final') continue; // ignore not-final
      if (playedIds.has(ev.id)) count++; else break;
    }
    return count;
  },[events, gs]);

  // Trainings totals and series (via signups 'training'); cancelled days do not break series, absence breaks
  const trainingsTotal = useMemo(()=>{
    const evMap = new Map(events.map(e=>[e.id, e]));
    return signups.filter(s=> (s.roles||[]).includes('training') && evMap.get(s.event_id)?.type==='training' && evMap.get(s.event_id)?.status !== 'cancelled').length;
  },[events, signups]);

  const trainingsSeries = useMemo(()=>{
    const trainings = events.filter(e=>e.type==='training').map(e=>({ ...e, _dt: parseDate(e.start_datetime)})).filter(e=>e._dt).sort((a,b)=>a._dt-b._dt);
    const yes = new Set(signups.filter(s=> (s.roles||[]).includes('training')).map(s=>s.event_id));
    let count = 0;
    for (let i = trainings.length-1; i>=0; i--) {
      const ev = trainings[i];
      if (ev.status === 'cancelled') continue; // cancelled does not break
      if (yes.has(ev.id)) count++; else break; // absence breaks
    }
    return count;
  },[events, signups]);

  // OCE last 3
  const oceLast3 = useMemo(()=>{
    const arr = gs.slice(-3).map(calcOCEFromGame).map(x=>x.OCE);
    return arr.length? (arr.reduce((a,b)=>a+b,0)/arr.length):0;
  },[gs]);

  // Averages from stats
  const statAgg = useMemo(()=>{
    if (!gs.length) return { ppm:0, pmAvg:0, pfAvg:0, ftPct:0, bestPts:0, bestPM:0, bestFtPct:0 };
    const totalMin = gs.reduce((a,b)=> a + (Number(b.minutes)||0), 0) || 1;
    const totalPts = gs.reduce((a,b)=> a + (Number(b.points)||0), 0);
    const totalPM = gs.reduce((a,b)=> a + (Number(b.plus_minus)||0), 0);
    const totalPF = gs.reduce((a,b)=> a + (Number(b.fouls)||0), 0);
    const totalFTm = gs.reduce((a,b)=> a + (Number(b.free_throws_made)||0), 0);
    const totalFTa = gs.reduce((a,b)=> a + (Number(b.free_throws_att)||0), 0) || 1;
    const bestPts = Math.max(...gs.map(g=>Number(g.points||0)));
    const bestPM = Math.max(...gs.map(g=>Number(g.plus_minus||0)));
    const bestFtPct = Math.max(...gs.map(g=>{ const a=Number(g.free_throws_att||0)||1; return (Number(g.free_throws_made||0)/a)*100; }));
    return {
      ppm: totalPts/totalMin,
      pmAvg: totalPM/gs.length,
      pfAvg: totalPF/gs.length,
      ftPct: (totalFTm/totalFTa)*100,
      bestPts, bestPM, bestFtPct
    };
  },[gs]);

  // Kiosk
  const kioskAgg = useMemo(()=>{
    const totalBeers = kdays.reduce((a,b)=> a + (Number(b.beers_at_kiosk)||0), 0);
    const uniqDays = new Set(kdays.map(d=>d.date)).size || 1;
    const sessCount = ksessions.length || 1;
    return {
      beersPerDay: totalBeers/uniqDays,
      beersPerSession: totalBeers/sessCount
    }
  },[kdays, ksessions]);

  const badges = useMemo(()=>{
    const out=[];
    const correctWins = bets.filter(b=> typeof b.points_awarded==='number' && b.points_awarded>=5).length;
    if (correctWins>=5) out.push("Tippspiel-König");
    if (kdays.some(d=> (d.beers_at_kiosk||0)>=10)) out.push("Kiosk-Kaiser");
    if (oceLast3>15) out.push("Mr. Efficiency");
    if (gs.length>=5) out.push("Der Fünfer");
    return out;
  },[bets,kdays,gs,oceLast3]);

  const minutesToday = (kdays.find(d=>d.date===new Date().toISOString().slice(0,10))?.minutes_at_kiosk)||0;
  const beersToday = (kdays.find(d=>d.date===new Date().toISOString().slice(0,10))?.beers_at_kiosk)||0;
  const { KCE } = calcKCE({ minutes: minutesToday, beers: beersToday });

  return (
    <div className="pb-28 min-h-screen bg-[#0e0b0c] text-rose-50 px-4 pt-6" data-testid="profile">
      {/* MVP Vote Button - Only show if there are open votes */}
      {openMvpEvents.length > 0 && (
        <div className="mb-4">
          <button
            onClick={() => {
              setSelectedMvpEvent(openMvpEvents[0]);
              setShowMvpModal(true);
            }}
            className="w-full px-4 py-3 rounded-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 font-bold flex items-center justify-center gap-2 shadow-lg animate-pulse"
          >
            <span className="text-2xl">🚿</span>
            <span>Dusch-Gel MVP wählen</span>
            <span className="bg-red-600 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center">
              {openMvpEvents.length}
            </span>
          </button>
        </div>
      )}
      
      <h2 className="text-xl font-bold mb-2">Mein Profil</h2>
      <div className="flex items-center gap-3 mb-1">
        <PlayerAvatar photoUrl={me?.photo_url} name={me?.display_name || user?.displayName || 'Spieler'} size="lg" />
        <p className="text-sm">{(me?.display_name || user?.displayName || user?.email || 'Spieler')}</p>
      </div>
      {me?.tna_number && (<p className="text-xs">TNA‑Nr: {me.tna_number}</p>)}
      
      {/* Profile Info Card */}
      <div className="mt-3 p-3 rounded-lg bg-gradient-to-r from-pink-900/30 to-purple-900/30 border border-pink-500/20">
        <div className="grid grid-cols-4 gap-2 text-center">
          <div>
            <div className="text-xs text-rose-200/50">Größe</div>
            <div className="font-bold">{me?.height ? `${me.height} cm` : '—'}</div>
          </div>
          <div>
            <div className="text-xs text-rose-200/50">Gewicht</div>
            <div className="font-bold">{me?.weight ? `${me.weight} kg` : '—'}</div>
          </div>
          <div>
            <div className="text-xs text-rose-200/50">Position</div>
            <div className="font-bold">{me?.position ? me.position.toUpperCase() : '—'}</div>
          </div>
          <div>
            <div className="text-xs text-rose-200/50">Email</div>
            <div className="font-bold text-xs truncate">{me?.email ? '✓' : '—'}</div>
          </div>
        </div>
        {(!me?.height || !me?.weight || !me?.position || !me?.email) && (
          <p className="text-xs text-yellow-400 mt-2 text-center">
            ⚠️ Profil unvollständig - bitte in Einstellungen ergänzen
          </p>
        )}
      </div>
      
      <div className="grid gap-3 mt-3">
        <div className="p-3 rounded-lg bg-[#1a0d11] border border-white/10">
          <div className="text-sm font-semibold">Serien</div>
          <div className="text-xs mt-1">Spiele in Folge (Stats): {gamesSeries} • Trainings in Folge: {trainingsSeries}</div>
        </div>
        <div className="p-3 rounded-lg bg-[#1a0d11] border border-white/10">
          <div className="text-sm font-semibold">Zähler</div>
          <div className="text-xs mt-1">Spiele gesamt: {gamesTotal} • Trainings gesamt: {trainingsTotal}</div>
        </div>
        <div className="p-3 rounded-lg bg-[#1a0d11] border border-white/10">
          <div className="text-sm font-semibold">Durchschnitte</div>
          <div className="text-xs mt-1">PPM: {statAgg.ppm.toFixed(3)} • Ø+/‑: {statAgg.pmAvg.toFixed(2)} • ØPF: {statAgg.pfAvg.toFixed(2)} • FW%: {statAgg.ftPct.toFixed(1)}%</div>
          <div className="text-xs mt-1">Bestwerte → Punkte: {statAgg.bestPts} • +/‑: {statAgg.bestPM} • FW% max: {statAgg.bestFtPct.toFixed(1)}%</div>
        </div>
        <div className="p-3 rounded-lg bg-[#1a0d11] border border-white/10">
          <div className="text-sm font-semibold">Kiosk</div>
          <div className="text-xs mt-1">Biere/Session: {kioskAgg.beersPerSession.toFixed(2)} • Biere/Tag: {kioskAgg.beersPerDay.toFixed(2)} • Heute KCE: {KCE.toFixed(2)}</div>
        </div>
        <div className="p-3 rounded-lg bg-[#1a0d11] border border-white/10">
          <div className="text-sm">Badges:</div>
          <div className="mt-1 text-xs">{badges.length? badges.join(" • "): "Noch keine Abzeichen"}</div>
        </div>
      </div>
      
      {/* User Settings Section */}
      <UserSettings user={user} me={me} />
      
      {/* MVP Voting Modal */}
      {showMvpModal && selectedMvpEvent && (
        <div className="fixed inset-0 bg-black/95 z-[100] flex items-center justify-center p-4">
          <Card className="bg-[#1a0d11] border-blue-500/50 max-w-md w-full border-2">
            <CardContent className="pt-6">
              <h3 className="text-xl font-bold mb-3 text-blue-400">
                🚿 Dusch-Gel MVP Vote
              </h3>
              <p className="text-sm mb-4 text-rose-200/90">
                Wer war der MVP bei "{selectedMvpEvent.title}"?
              </p>
              
              <div className="space-y-3 mb-4">
                <div>
                  <label className="text-sm text-rose-200/90 block mb-2">Spieler auswählen:</label>
                  <select
                    value={mvpVote}
                    onChange={(e) => setMvpVote(e.target.value)}
                    className="w-full p-2 rounded bg-[#2a1419] text-white border border-blue-500/30 focus:border-blue-500"
                  >
                    <option value="">Wähle einen Spieler...</option>
                    {users.filter(u => {
                      // Only show users who participated in the event
                      const participatedInEvent = signups.some(s => 
                        s.event_id === selectedMvpEvent.id && 
                        s.user_id === u.id && 
                        (s.roles || []).length > 0
                      );
                      return participatedInEvent && (u.role === 'player' || u.role === 'sparring');
                    }).map(u => (
                      <option key={u.id} value={u.id}>
                        {u.display_name || u.name || u.email?.split('@')[0] || 'Unbekannt'}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="flex gap-2">
                <Button 
                  onClick={submitMvpVote}
                  className="flex-1 bg-blue-700 hover:bg-blue-800 text-white font-bold"
                  disabled={!mvpVote}
                >
                  🏆 Vote abgeben
                </Button>
                <Button 
                  onClick={() => {
                    setShowMvpModal(false);
                    setMvpVote("");
                    setSelectedMvpEvent(null);
                  }}
                  className="flex-1 bg-white/10 hover:bg-white/20 text-sm"
                >
                  Abbrechen
                </Button>
              </div>
              
              <p className="text-xs text-rose-200/60 mt-3 text-center">
                Deine Stimme für den besten Spieler des Events
              </p>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

// User Settings Component
function UserSettings({ user, me }) {
  const [showSettings, setShowSettings] = useState(false);
  const [email, setEmail] = useState(me?.email || user?.email || '');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [statusMsg, setStatusMsg] = useState('');
  
  // Profile data
  const [height, setHeight] = useState(me?.height || '');
  const [weight, setWeight] = useState(me?.weight || '');
  const [position, setPosition] = useState(me?.position || '');
  const [profileSaving, setProfileSaving] = useState(false);
  
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || (import.meta && import.meta.env && import.meta.env.REACT_APP_BACKEND_URL);
  
  const POSITIONS = [
    { id: 'pg', name: 'Point Guard (1)' },
    { id: 'sg', name: 'Shooting Guard (2)' },
    { id: 'sf', name: 'Small Forward (3)' },
    { id: 'pf', name: 'Power Forward (4)' },
    { id: 'c', name: 'Center (5)' },
  ];
  
  // Update profile data (height, weight, position)
  const updateProfile = async () => {
    setProfileSaving(true);
    try {
      const { updateDoc, doc } = await import('firebase/firestore');
      const { db } = await import('@/config/firebase');
      
      await updateDoc(doc(db, 'users', user.uid), {
        height: height ? parseInt(height) : null,
        weight: weight ? parseInt(weight) : null,
        position: position || null,
        profile_updated_at: new Date()
      });
      
      setStatusMsg('✅ Profildaten gespeichert!');
    } catch (error) {
      setStatusMsg(`❌ Fehler: ${error.message}`);
    }
    setProfileSaving(false);
  };
  
  const updateEmail = async () => {
    if (!email.trim()) {
      setStatusMsg('❌ Bitte Email eingeben');
      return;
    }
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/user/update_email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: user.uid,
          email: email.trim()
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setStatusMsg('✅ Email aktualisiert!');
      } else {
        setStatusMsg(`❌ Fehler: ${data.message || 'Unbekannter Fehler'}`);
      }
    } catch (error) {
      setStatusMsg(`❌ Fehler: ${error.message}`);
    }
  };
  
  const updatePassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      setStatusMsg('❌ Alle Passwort-Felder ausfüllen');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setStatusMsg('❌ Passwörter stimmen nicht überein');
      return;
    }
    
    if (newPassword.length < 6) {
      setStatusMsg('❌ Passwort muss mindestens 6 Zeichen haben');
      return;
    }
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/user/update_password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: user.uid,
          current_password: currentPassword,
          new_password: newPassword
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setStatusMsg('✅ Passwort geändert!');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        setStatusMsg(`❌ Fehler: ${data.message || 'Unbekannter Fehler'}`);
      }
    } catch (error) {
      setStatusMsg(`❌ Fehler: ${error.message}`);
    }
  };
  
  return (
    <div className="mt-6 mb-4">
      <button 
        onClick={() => setShowSettings(!showSettings)}
        className="w-full p-3 rounded-lg bg-[#1a0d11] border border-white/10 text-left font-semibold flex justify-between items-center"
      >
        <span>⚙️ Einstellungen</span>
        <span>{showSettings ? '▼' : '▶'}</span>
      </button>
      
      {showSettings && (
        <div className="mt-3 space-y-4 p-4 rounded-lg bg-[#160b0f] border border-white/10">
          {/* Profile Data */}
          <div className="space-y-3">
            <label className="block text-sm font-semibold mb-2">👤 Profildaten</label>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-rose-200/60 mb-1">Größe (cm)</label>
                <input
                  type="number"
                  value={height}
                  onChange={(e) => setHeight(e.target.value)}
                  placeholder="z.B. 185"
                  className="w-full p-2 rounded bg-[#1a0d11] border border-white/10 text-rose-50 text-sm"
                  data-testid="profile-height-input"
                />
              </div>
              <div>
                <label className="block text-xs text-rose-200/60 mb-1">Gewicht (kg)</label>
                <input
                  type="number"
                  value={weight}
                  onChange={(e) => setWeight(e.target.value)}
                  placeholder="z.B. 80"
                  className="w-full p-2 rounded bg-[#1a0d11] border border-white/10 text-rose-50 text-sm"
                  data-testid="profile-weight-input"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-xs text-rose-200/60 mb-1">Lieblingsposition</label>
              <select
                value={position}
                onChange={(e) => setPosition(e.target.value)}
                className="w-full p-2 rounded bg-[#1a0d11] border border-white/10 text-rose-50 text-sm"
                data-testid="profile-position-select"
              >
                <option value="">Position wählen...</option>
                {POSITIONS.map(pos => (
                  <option key={pos.id} value={pos.id}>{pos.name}</option>
                ))}
              </select>
            </div>
            
            <button
              onClick={updateProfile}
              disabled={profileSaving}
              className="w-full p-2 rounded bg-purple-700 hover:bg-purple-800 text-sm font-semibold disabled:opacity-50"
              data-testid="save-profile-btn"
            >
              {profileSaving ? 'Speichern...' : 'Profildaten speichern'}
            </button>
          </div>
          
          <hr className="border-white/10" />
          
          {/* Email Update */}
          <div>
            <label className="block text-sm font-semibold mb-2">📧 Email-Adresse</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="deine@email.de"
              className="w-full p-2 rounded bg-[#1a0d11] border border-white/10 text-rose-50 text-sm mb-2"
            />
            <button
              onClick={updateEmail}
              className="w-full p-2 rounded bg-blue-700 hover:bg-blue-800 text-sm font-semibold"
            >
              Email aktualisieren
            </button>
          </div>
          
          {/* Password Update */}
          <div>
            <label className="block text-sm font-semibold mb-2">🔒 Passwort ändern</label>
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              placeholder="Aktuelles Passwort"
              className="w-full p-2 rounded bg-[#1a0d11] border border-white/10 text-rose-50 text-sm mb-2"
            />
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Neues Passwort (min. 6 Zeichen)"
              className="w-full p-2 rounded bg-[#1a0d11] border border-white/10 text-rose-50 text-sm mb-2"
            />
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Neues Passwort bestätigen"
              className="w-full p-2 rounded bg-[#1a0d11] border border-white/10 text-rose-50 text-sm mb-2"
            />
            <button
              onClick={updatePassword}
              className="w-full p-2 rounded bg-green-700 hover:bg-green-800 text-sm font-semibold"
            >
              Passwort ändern
            </button>
          </div>
          
          {/* Status Message */}
          {statusMsg && (
            <div className={`p-2 rounded text-sm ${statusMsg.includes('✅') ? 'bg-green-900/30 text-green-400' : 'bg-red-900/30 text-red-400'}`}>
              {statusMsg}
            </div>
          )}
          
          <p className="text-xs text-rose-200/60">
            ℹ️ Email wird für Passwort-Zurücksetzen verwendet
          </p>
        </div>
      )}
    </div>
  );
}
